import { LayerTypeLayerSummaryProps } from "../../../types";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useSelectionSidebarLoading } from "../../../Bundle/useSelectionSidebarLoadingState";
import {
  DesignationDistance,
  useNearestDesignationsQuery,
} from "react-migration/domains/constraints/designation/hooks/useNearestDesignationQuery";
import { Insight } from "react-migration/components/Insight/Insight";
import { KeyIndicatorColor, KeyIndicatorTag, useKeyIndicatorTags } from "react-migration/lib/map/useKeyIndicatorTags";

const getDesignationAttribute = (designation: DesignationDistance["designation"] | undefined, key: string) => {
  return designation?.designation_attributes.find((f) => f.key === key)?.value;
};

const subCategories = [
  "highway_maintenance_public",
  "highway_maintenance_prospective",
  "highway_maintenance_nonpublic",
  "highway_maintenance_unclassified",
] as const;

type AdoptedRoadCategory = (typeof subCategories)[number];

const subCategoryToLabelMap: Record<AdoptedRoadCategory, KeyIndicatorTag[]> = {
  highway_maintenance_public: [
    {
      color: KeyIndicatorColor.GREEN,
      tag: "Adopted",
    },
  ],
  highway_maintenance_prospective: [
    {
      color: KeyIndicatorColor.ORANGE,
      tag: "Pending adoption",
    },
  ],
  highway_maintenance_nonpublic: [
    {
      color: KeyIndicatorColor.RED,
      tag: "Unadopted",
    },
  ],
  highway_maintenance_unclassified: [
    {
      color: KeyIndicatorColor.DEFAULT,
      tag: "Unclassified",
    },
  ],
};

export function AdoptedRoadsSummary({ selection, onLoadingChange, print }: LayerTypeLayerSummaryProps) {
  const { distances: designations, loading } = useNearestDesignationsQuery(
    selection.feature?.geometry,
    subCategories.map((key) => ({ category: key, radiusMeters: 100 }))
  );

  useSelectionSidebarLoading(onLoadingChange, loading);

  const closest = [...(designations ?? [])].sort((a, b) => a.distanceMeters - b.distanceMeters)[0]?.designation;

  useKeyIndicatorTags(() => {
    if (closest?.sub_category_id) {
      return subCategoryToLabelMap[closest.sub_category_id as AdoptedRoadCategory];
    }

    return [];
  }, [closest?.sub_category_id]);

  if (loading) {
    return (
      <div className="relative p-2 h-[168px]">
        <SkeletonLoading cols={2} rows={1} showPadding={false} bgClassName="bg-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-2">
        <Insight.Wrapper title="Status">
          <Insight.Body>{closest?.display_name}</Insight.Body>
          <Insight.Footer>
            {getDesignationAttribute(closest, "maintenance_authority_name") ||
              getDesignationAttribute(closest, "highway_authority_name") ||
              "Unknown"}
          </Insight.Footer>
        </Insight.Wrapper>
      </div>
    </div>
  );
}

AdoptedRoadsSummary.icon = "icon-lt-traffic-line";
AdoptedRoadsSummary.title = "Nearest road adoption status";
